import React, { useCallback, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  useIsMobile,
} from '@ais-ui/ui-core';
import { ArrowRight, MapPin, ZoomIn } from 'lucide-react';
import { PiArrowSquareOut } from 'react-icons/pi';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { usePorts } from '../Ports/PortProvider';
import { cn, locodeToCountry } from '@ais-ui/utils';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { ShipInfo } from '@ais-ui/websockets';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

export interface LastPortCallData {
  portName: string;
  locode: string;
  country?: string;
  arrivalTime?: Date;
  departureTime?: Date;
  latitude?: string;
  longitude?: string;
}

interface LastPortCallCardProps {
  portCall: LastPortCallData;
  showActions?: boolean;
  imo?: string; // Add IMO prop
}

// Component that only renders when map is available
function LastPortCallCardWithMap({
  portCall,
  showActions = true,
  imo,
}: LastPortCallCardProps) {
  const [open, setOpen] = useState(false);
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { portsByLocode, zoomToPort } = usePorts();
  const { currentTargetInfo } = useSelectionState();
  const { map } = useOpenLayersContext(); // Safe to call here because we only render when map is available

  const handleClose = () => {
    setOpen(false);
  };

  const onZoomToPort = useCallback(() => {
    if (!map || !portCall.locode) return;

    // First try to get the port from our PortProvider using the locode
    const port = portsByLocode.get(portCall.locode);

    if (port) {
      // Use the shared zoomToPort function with port data
      zoomToPort(port, map);
    } else if (portCall.latitude && portCall.longitude) {
      // Fallback: create a port-like object with position data
      const portLikeObject = {
        position: {
          coordinates: [
            parseFloat(portCall.longitude),
            parseFloat(portCall.latitude),
          ],
        },
      };
      zoomToPort(portLikeObject, map);
    } else {
      console.warn(
        `Unable to zoom to port: ${portCall.locode}. No coordinates available.`,
      );
    }
  }, [
    map,
    portCall.locode,
    portCall.latitude,
    portCall.longitude,
    portsByLocode,
    zoomToPort,
  ]);

  // Only show timeline button if vessel has IMO
  const handleViewTimeline = useCallback(() => {
    if (!imo) return;
    const targetInfo = currentTargetInfo as ShipInfo;
    const basic =
      currentTargetInfo && 'basic' in currentTargetInfo
        ? (currentTargetInfo as unknown as { basic?: Record<string, unknown> })
            .basic
        : undefined;
    const mmsi = targetInfo?.mmsi || basic?.mmsi;
    const vesselName = targetInfo?.name || (basic?.vesselName as string) || '';
    openPopupOnSecondaryScreen(PopupType.VesselTimeline, {
      imo: String(imo),
      mmsi: mmsi ? String(mmsi) : '',
      name: vesselName || '',
      // Serialize shipInfo for the popup's vessel details panel
      shipInfo: JSON.stringify({
        name: targetInfo?.name,
        imo: targetInfo?.imo,
        mmsi: targetInfo?.mmsi,
        callSign: targetInfo?.callSign,
        country: targetInfo?.country,
        aisType: targetInfo?.aisType,
        sourceType: targetInfo?.sourceType,
        shipType: targetInfo?.shipType,
        width: targetInfo?.width,
        length: targetInfo?.length,
        draught: targetInfo?.draught,
        heading: targetInfo?.heading,
        rateOfTurn: targetInfo?.rateOfTurn,
        courseOverGround: targetInfo?.courseOverGround,
        speedOverGround: targetInfo?.speedOverGround,
        destination: targetInfo?.destination,
        navigationalStatus: targetInfo?.navigationalStatus,
        eta: targetInfo?.eta?.toISOString?.() || targetInfo?.eta,
        portEta: targetInfo?.portEta?.toISOString?.() || targetInfo?.portEta,
        lastAisLatitude: targetInfo?.lastAisLatitude,
        lastAisLongitude: targetInfo?.lastAisLongitude,
        lastAisReport:
          targetInfo?.lastAisReport?.toISOString?.() ||
          targetInfo?.lastAisReport,
        lastAisSatLatitude: targetInfo?.lastAisSatLatitude,
        lastAisSatLongitude: targetInfo?.lastAisSatLongitude,
        lastAisSatReport:
          targetInfo?.lastAisSatReport?.toISOString?.() ||
          targetInfo?.lastAisSatReport,
      }),
    });
  }, [imo, currentTargetInfo]);

  const onViewTimeline = imo ? handleViewTimeline : null;

  return (
    <LastPortCallCardContent
      portCall={portCall}
      showActions={showActions}
      open={open}
      setOpen={setOpen}
      onZoomToPort={onZoomToPort}
      onViewTimeline={onViewTimeline}
      handleClose={handleClose}
      formatDateTimeWithTimeZone={formatDateTimeWithTimeZone}
    />
  );
}

// Component for when map is not available
function LastPortCallCardWithoutMap({
  portCall,
  showActions = true,
  imo,
}: LastPortCallCardProps) {
  const [open, setOpen] = useState(false);
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { currentTargetInfo } = useSelectionState();

  const handleClose = () => {
    setOpen(false);
  };

  // Only show timeline button if vessel has IMO
  const handleViewTimeline = useCallback(() => {
    if (!imo) return;
    const targetInfo = currentTargetInfo as ShipInfo;
    const basic =
      currentTargetInfo && 'basic' in currentTargetInfo
        ? (currentTargetInfo as unknown as { basic?: Record<string, unknown> })
            .basic
        : undefined;
    const mmsi = targetInfo?.mmsi || basic?.mmsi;
    const vesselName = targetInfo?.name || (basic?.vesselName as string) || '';
    openPopupOnSecondaryScreen(PopupType.VesselTimeline, {
      imo: String(imo),
      mmsi: mmsi ? String(mmsi) : '',
      name: vesselName || '',
      // Serialize shipInfo for the popup's vessel details panel
      shipInfo: JSON.stringify({
        name: targetInfo?.name,
        imo: targetInfo?.imo,
        mmsi: targetInfo?.mmsi,
        callSign: targetInfo?.callSign,
        country: targetInfo?.country,
        aisType: targetInfo?.aisType,
        sourceType: targetInfo?.sourceType,
        shipType: targetInfo?.shipType,
        width: targetInfo?.width,
        length: targetInfo?.length,
        draught: targetInfo?.draught,
        heading: targetInfo?.heading,
        rateOfTurn: targetInfo?.rateOfTurn,
        courseOverGround: targetInfo?.courseOverGround,
        speedOverGround: targetInfo?.speedOverGround,
        destination: targetInfo?.destination,
        navigationalStatus: targetInfo?.navigationalStatus,
        eta: targetInfo?.eta?.toISOString?.() || targetInfo?.eta,
        portEta: targetInfo?.portEta?.toISOString?.() || targetInfo?.portEta,
        lastAisLatitude: targetInfo?.lastAisLatitude,
        lastAisLongitude: targetInfo?.lastAisLongitude,
        lastAisReport:
          targetInfo?.lastAisReport?.toISOString?.() ||
          targetInfo?.lastAisReport,
        lastAisSatLatitude: targetInfo?.lastAisSatLatitude,
        lastAisSatLongitude: targetInfo?.lastAisSatLongitude,
        lastAisSatReport:
          targetInfo?.lastAisSatReport?.toISOString?.() ||
          targetInfo?.lastAisSatReport,
      }),
    });
  }, [imo, currentTargetInfo]);

  const onViewTimeline = imo ? handleViewTimeline : null;

  // No zoom functionality when map is not available
  const onZoomToPort = null;

  return (
    <LastPortCallCardContent
      portCall={portCall}
      showActions={showActions}
      open={open}
      setOpen={setOpen}
      onZoomToPort={onZoomToPort}
      onViewTimeline={onViewTimeline}
      handleClose={handleClose}
      formatDateTimeWithTimeZone={formatDateTimeWithTimeZone}
    />
  );
}

// Shared UI component that doesn't use any hooks
interface LastPortCallCardContentProps {
  portCall: LastPortCallData;
  showActions: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  onZoomToPort: (() => void) | null;
  onViewTimeline: (() => void) | null; // Make optional since it depends on IMO
  handleClose: () => void;
  formatDateTimeWithTimeZone: (date: Date) => string;
}

function LastPortCallCardContent({
  portCall,
  showActions,
  open,
  setOpen,
  onZoomToPort,
  onViewTimeline,
  handleClose,
  formatDateTimeWithTimeZone,
}: LastPortCallCardContentProps) {
  const { viewMode } = useViewMode();
  const isMobile = useIsMobile();
  const isDetailView = viewMode === ViewMode.Detail;
  const isMapAvailable =
    viewMode === ViewMode.Normal || viewMode === ViewMode.Replay;

  const cardContent = (
    <div className="space-y-3">
      <div>
        <h4 className="font-semibold text-base">{portCall.portName}</h4>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{locodeToCountry(portCall.locode)}</span>
        </div>
      </div>

      <Separator />

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Locode:</span>
          <span className="font-medium">{portCall.locode}</span>
        </div>
        {portCall.arrivalTime && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Arrival:</span>
            <span className="font-medium text-xs">
              {formatDateTimeWithTimeZone(portCall.arrivalTime)}
            </span>
          </div>
        )}
        {portCall.departureTime ? (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Departure:</span>
            <span className="font-medium text-xs">
              {formatDateTimeWithTimeZone(portCall.departureTime)}
            </span>
          </div>
        ) : portCall.arrivalTime ? (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status:</span>
            <span className="font-medium text-xs text-green-600">In Port</span>
          </div>
        ) : null}
      </div>

      {portCall.latitude && portCall.longitude && (
        <>
          <Separator />
          <div className="text-xs text-muted-foreground">
            <p>
              Position: {portCall.latitude}, {portCall.longitude}
            </p>
          </div>
        </>
      )}

      {showActions && (onZoomToPort || onViewTimeline) && (
        <>
          <Separator />
          <div className="flex gap-2">
            {onZoomToPort && (
              <button
                className="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background border border-input hover:bg-accent hover:text-accent-foreground h-9 px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onZoomToPort();
                  handleClose();
                }}
              >
                <ZoomIn className="h-3 w-3 mr-1" />
                Zoom to Port
              </button>
            )}
            {onViewTimeline && (
              <button
                className="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewTimeline();
                  handleClose();
                }}
              >
                <PiArrowSquareOut className="h-3 w-3 mr-1" />
                View Timeline
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  // In detail view or mobile, render as disabled (no popover functionality)
  if (isDetailView || isMobile) {
    return (
      <div
        className={cn(
          'rounded-lg border border-border bg-card p-3',
          'opacity-50 cursor-not-allowed pointer-events-none select-none',
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-full p-2 bg-muted/50">
              <MapPin className="size-4 text-muted-foreground" />
            </div>
            <div>
              <p className="font-semibold text-sm text-muted-foreground">
                {portCall.portName}
              </p>
              <p className="text-xs text-muted-foreground">
                {locodeToCountry(portCall.locode)}
              </p>
            </div>
          </div>
          <ArrowRight className="size-4 text-muted-foreground" />
        </div>
      </div>
    );
  }

  // In normal/replay view, render with Popover functionality
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'group rounded-lg border border-border bg-card p-3 transition-all',
            isMapAvailable
              ? 'cursor-pointer hover:border-primary hover:shadow-md'
              : 'opacity-50 cursor-not-allowed',
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'rounded-full p-2',
                  isMapAvailable ? 'bg-primary/10' : 'bg-muted/50',
                )}
              >
                <MapPin
                  className={cn(
                    'size-4',
                    isMapAvailable ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
              </div>
              <div>
                <p
                  className={cn(
                    'font-semibold text-sm',
                    !isMapAvailable && 'text-muted-foreground',
                  )}
                >
                  {portCall.portName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {locodeToCountry(portCall.locode)}
                </p>
              </div>
            </div>
            <ArrowRight
              className={cn(
                'size-4 text-muted-foreground transition-colors',
                isMapAvailable && 'group-hover:text-primary',
              )}
            />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent side="left" align="end" className="w-80" sideOffset={8}>
        {cardContent}
      </PopoverContent>
    </Popover>
  );
}

// Main component that decides which variant to render
export function LastPortCallCard({
  portCall,
  showActions = true,
  imo,
}: LastPortCallCardProps) {
  const { viewMode } = useViewMode();

  // Only use map-enabled version in Normal and Replay modes
  const isMapAvailable =
    viewMode === ViewMode.Normal || viewMode === ViewMode.Replay;

  if (isMapAvailable) {
    return (
      <LastPortCallCardWithMap
        portCall={portCall}
        showActions={showActions}
        imo={imo}
      />
    );
  } else {
    return (
      <LastPortCallCardWithoutMap
        portCall={portCall}
        showActions={showActions}
        imo={imo}
      />
    );
  }
}

// Integrated version for your BaseInformation component
export function LastPortCallIntegrated({
  portCall,
  imo,
}: {
  portCall: LastPortCallData | null;
  imo?: string;
}) {
  if (!portCall) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <MapPin className="size-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">
          No port call history available
        </p>
      </div>
    );
  }

  return <LastPortCallCard portCall={portCall} showActions={true} imo={imo} />;
}
